import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageHeader from "../../../components/PageHeader";
import DeleteModal from "../../../components/modals/DeleteModal";
import { getContainersList } from "../../../services/container";
import Loading from "../../../components/utils/Loading";
import ZoneSettingsContenant from "./ZoneSettingsContenant";
import ZoneSettingsMaterials from "./ZoneSettingsMaterials";
import ZoneSettingsManagers from "./ZoneSettingsManagers";
import MidCycleFluxChangeBadge from "../../container/MidCycleFluxChangeBadge";
import SettingsChangeHistoryPanel from "../SettingsChangeHistoryPanel";

const TAB_IDS = {
  contenant: "contenant",
  materials: "materials",
  managers: "managers",
  modifications: "modifications",
};

const tabFromSearch = (search) => {
  const params = new URLSearchParams(search);
  const t = params.get("tab");
  return t && Object.values(TAB_IDS).includes(t) ? t : TAB_IDS.contenant;
};

/**
 * Nom d'affichage de la zone (ex: "Zone A", "Quai 3", ou adresse)
 */
const getZoneDisplayName = (container) => {
  if (!container) return "";
  if (container.positioning) return container.positioning;
  if (container.addressName) return container.addressName;
  if (container.containerId) return container.containerId;
  return container.name || container._id || "";
};

const ZoneSettingsPage = ({ token }) => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();

  const [container, setContainer] = useState(location.state?.container ?? null);
  const [containerDraft, setContainerDraft] = useState(
    location.state?.container ?? null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const initialTab = useMemo(
    () => tabFromSearch(location.search),
    [location.search],
  );
  const [activeTab, setActiveTab] = useState(initialTab);
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const locationStateRef = useRef(location.state);
  locationStateRef.current = location.state;

  useEffect(() => {
    setActiveTab(tabFromSearch(location.search));
  }, [location.search]);

  useEffect(() => {
    const stateContainer = locationStateRef.current?.container;
    if (!id) {
      if (stateContainer) {
        setContainer(stateContainer);
        setContainerDraft(stateContainer);
      }
      setIsLoading(false);
      return;
    }
    getContainersList({
      page: null,
      limit: null,
      fieldSort: "addressName",
      orderSort: "1",
      keywordFilter: JSON.stringify([[], [], [], [], [], []]),
      arrayFilterFillingLevel: JSON.stringify([0, 100]),
      typeFillingFilter: "all",
      stopTime: "",
    })
      .then((response) => {
        if (response?.status === 200 && Array.isArray(response.data?.data)) {
          const found = response.data.data.find(
            (c) => c._id === id || c._measurement === id,
          );
          const next = found ?? locationStateRef.current?.container ?? null;
          setContainer(next);
          setContainerDraft(next);
        } else {
          const fallback = locationStateRef.current?.container ?? null;
          setContainer(fallback);
          setContainerDraft(fallback);
        }
        setIsLoading(false);
      })
      .catch(() => {
        const fallback = locationStateRef.current?.container ?? null;
        setContainer(fallback);
        setContainerDraft(fallback);
        setIsLoading(false);
      });
  }, [id]);

  const zoneName = getZoneDisplayName(container);
  const from = location.state?.from;
  const returnPath = location.state?.returnPath;
  const { backRoute, backLabel } = (() => {
    if (from === "settings") {
      return {
        backRoute: "/settings/containers",
        backLabel: t("containerSettings.backToZoneList"),
      };
    }
    if (from === "characterization") {
      return {
        backRoute: returnPath || "/characterization/list",
        backLabel: t("containerSettings.backToCharacterization"),
      };
    }
    if (from === "monitoring") {
      return {
        backRoute: returnPath || "/containers",
        backLabel: container?.addressName
          ? t("containerSettings.backToSite", { site: container.addressName })
          : t("containerSettings.backToContainers"),
      };
    }
    return {
      backRoute: "/settings/containers",
      backLabel: container?.addressName
        ? t("containerSettings.backToSite", { site: container.addressName })
        : t("containerSettings.backToZones"),
    };
  })();
  const pageTitle = zoneName
    ? t("containerSettings.settingsForZone", { name: zoneName })
    : t("containerSettings.zoneSettings");

  const tabs = [
    { id: TAB_IDS.contenant, label: t("Container") },
    {
      id: TAB_IDS.materials,
      label: t("containerSettings.unauthorizedMaterials", {
        defaultValue: "Matériaux non autorisés",
      }),
    },
    {
      id: TAB_IDS.managers,
      label: t("containerSettings.managers"),
    },
    {
      id: TAB_IDS.modifications,
      label: t("settingsHistory.tab"),
    },
  ];

  if (!token) return null;
  if (isLoading) return <Loading color="#F5F5F5" />;
  if (!container) {
    history.replace("/settings/containers");
    return null;
  }

  const handleSave = () => {
    document.getElementById("zone-settings-contenant-form")?.requestSubmit();
  };

  const handleContainerSaved = (updatedContainer) => {
    if (updatedContainer && typeof updatedContainer === "object") {
      setContainer((prev) => {
        const next = prev ? { ...prev, ...updatedContainer } : updatedContainer;
        setContainerDraft(next);
        return next;
      });
    }
  };

  const handleMidCycleFluxValidated = (updatedContainer) => {
    if (updatedContainer && typeof updatedContainer === "object") {
      setContainer((prev) => {
        const next = prev ? { ...prev, ...updatedContainer } : updatedContainer;
        setContainerDraft(next);
        return next;
      });
    }
  };

  const handleContainerDraftChange = (draftContainer) => {
    if (!draftContainer || typeof draftContainer !== "object") return;
    setContainerDraft(draftContainer);
  };

  const handleModifyFlow = () => {
    setActiveTab(TAB_IDS.contenant);
    history.replace({
      pathname: location.pathname,
      search: "?tab=contenant&scrollTo=gestion",
    });
  };

  const actionsMenuItems = container?._id
    ? [
        {
          label: t("settingsContainers.deleteZone"),
          icon: <i className="far fa-trash-alt text-[#222222]" aria-hidden />,
          onClick: () => setShowDeleteModal(true),
        },
      ]
    : null;

  return (
    <div className="w-full zone-settings">
      <PageHeader
        showBackButton
        backRoute={backRoute}
        backLabel={backLabel}
        title={pageTitle}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        actionsMenuItems={actionsMenuItems}
      />
      <div className="zone-settings__content-area w-full overflow-x-auto bg-[#F5F5F5] min-h-[50vh] md:min-h-[60vh]">
        <div className="zone-settings__content-inner">
          <div
            className={
              activeTab === TAB_IDS.modifications
                ? "zone-settings__tab-stack zone-settings__tab-stack--table"
                : "zone-settings__tab-stack"
            }
          >
            {container?.midCycleFluxChange?.active ? (
              <div className="zone-settings__midcycle-alert">
                <MidCycleFluxChangeBadge
                  container={container}
                  onValidated={handleMidCycleFluxValidated}
                />
              </div>
            ) : null}
            <div
              className={
                activeTab === TAB_IDS.contenant
                  ? "zone-settings__tab-panel zone-settings__tab-panel--contenant"
                  : "zone-settings__tab-panel zone-settings__tab-panel--contenant hidden"
              }
            >
              <ZoneSettingsContenant
                container={container}
                token={token}
                onFormChange={setHasFormChanges}
                onContainerSaved={handleContainerSaved}
                onContainerDraftChange={handleContainerDraftChange}
              />
            </div>
            <div
              className={
                activeTab === TAB_IDS.materials
                  ? "zone-settings__tab-panel"
                  : "zone-settings__tab-panel hidden"
              }
            >
              <ZoneSettingsMaterials
                container={containerDraft || container}
                onModifyFlow={handleModifyFlow}
              />
            </div>
            {activeTab === TAB_IDS.managers && (
              <div className="zone-settings__tab-panel">
                <ZoneSettingsManagers container={container} />
              </div>
            )}
            {activeTab === TAB_IDS.modifications && (
              <div className="zone-settings__tab-panel zone-settings__tab-panel--table">
                <SettingsChangeHistoryPanel
                  entityType="container"
                  entityId={container._id}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {hasFormChanges && (
        <footer className="zone-settings__footer zone-settings__footer--full zone-settings__footer--sticky">
          <div className="zone-settings__footer-inner">
            <div className="zone-settings__footer-actions">
              <button
                type="button"
                className="modal-button modal-button-cancel"
                onClick={() => history.push(backRoute)}
              >
                {t("containerSettings.cancel")}
              </button>
              <button
                type="button"
                className="modal-button modal-button-save"
                onClick={handleSave}
              >
                {t("containerSettings.save")}
              </button>
            </div>
          </div>
        </footer>
      )}

      {showDeleteModal && container?._id ? (
        <DeleteModal
          modal={showDeleteModal}
          setModal={setShowDeleteModal}
          id={container._id}
          message1={t("settingsContainers.deleteConfirm", { name: zoneName })}
          message2={zoneName}
          messageValidation={t("settingsContainers.zoneDeletedSuccess")}
          path="container/delete/"
          tableData={[]}
          setTableData={() => {}}
          setIsDeleted={() => history.push("/settings/containers")}
        />
      ) : null}
    </div>
  );
};

export default ZoneSettingsPage;
